import axios from "axios";
import { Capacitor } from "@capacitor/core";
import {
  isCacheableGet, cacheKey, saveCache, readCache,
  isQueueableWrite, enqueueWrite, sessionEpoch, purgeLegacyReadCache,
} from "@/lib/offline";
import { saveOfflineFile } from "@/lib/offlineFiles";
import { clientPlatform, clientVersion, initClientVersion } from "@/lib/clientInfo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

initClientVersion();

// Inside the native app the web view runs on a different origin
// (https://localhost), so credentialed cross-origin requests clash with the
// backend's wildcard CORS and fail. Native auth uses the Bearer token instead,
// so disable cookies there; the web build stays same-origin with cookies.
const IS_NATIVE = (() => { try { return Capacitor.isNativePlatform(); } catch { return false; } })();

const api = axios.create({
  baseURL: API,
  withCredentials: !IS_NATIVE,
});

purgeLegacyReadCache();
const staleSession = (config) => config?.__flSessionEpoch !== sessionEpoch();
const sessionError = () => new axios.CanceledError("Authenticated session changed");
let sessionController = new AbortController();
const cancelPreviousSession = () => {
  sessionController.abort();
  sessionController = new AbortController();
};
window.addEventListener('fieldlink:session-changed', cancelPreviousSession);
window.addEventListener('storage', (event) => {
  if (event.key === 'fl_api_session_epoch') cancelPreviousSession();
});

// Attach Bearer token as fallback
api.interceptors.request.use((config) => {
  config.signal = config.signal || sessionController.signal;
  config.__flSessionEpoch = sessionEpoch();
  config.__flScheduleIdentity = localStorage.getItem("fl_schedule_cache_identity") || null;
  const token = localStorage.getItem("fl_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Report app build + platform so super-admin can see who is on the latest
  // release and what device type each user is on. Harmless extra headers.
  try {
    config.headers["X-FL-Client-Platform"] = clientPlatform();
    const v = clientVersion();
    if (v) config.headers["X-FL-Client-Version"] = v;
  } catch { /* never block a request over telemetry */ }
  // Poor reception: don't let field-critical writes hang indefinitely. On
  // timeout axios raises a no-response error, which the response interceptor
  // queues offline to auto-sync later — so the worker gets instant feedback
  // instead of a frozen "Submitting…" button. Office long-running calls
  // (exports, payroll runs) aren't queueable, so they keep their own timing.
  if (config.timeout == null && isQueueableWrite(config.method || "get", config.url || "")) {
    config.timeout = 15000;
  }
  return config;
});

// Silent session refresh — one in-flight refresh shared by concurrent 401s.
let refreshPromise = null;
let refreshEpoch = null;
function trySilentRefresh() {
  const epoch = sessionEpoch();
  if (!refreshPromise || refreshEpoch !== epoch) {
    refreshEpoch = epoch;
    const stored = localStorage.getItem("fl_refresh");
    refreshPromise = axios
      .post(`${API}/auth/refresh`, stored ? { refresh_token: stored } : {}, { withCredentials: !IS_NATIVE, signal: sessionController.signal, timeout: 15000 })
      .then(({ data }) => {
        if (epoch !== sessionEpoch()) throw sessionError();
        if (data?.access_token) localStorage.setItem("fl_token", data.access_token);
        if (data?.refresh_token) localStorage.setItem("fl_refresh", data.refresh_token);
        return data?.access_token || null;
      })
      .catch((error) => {
        // Only a rejected refresh credential ends a session. Network/5xx failures
        // must reach the caller without deleting tokens or redirecting to login.
        if ([400, 401, 403].includes(error.response?.status)) return null;
        throw error;
      })
      .finally(() => {
        setTimeout(() => { if (refreshEpoch === epoch) refreshPromise = null; }, 0);
      });
  }
  return refreshPromise;
}

// Globally handle 401s for authenticated requests
// - /auth/login: let bad-creds errors propagate to the form
// - /auth/me: let the AuthContext silently set user=false
// - Anything else: silently redirect to /login (cookie expired or never authenticated)
api.interceptors.response.use(
  (response) => {
    // Offline support: keep a local copy of field-critical GET responses
    // so the app can render last-known data with no reception.
    const cfg = response?.config || {};
    if (staleSession(cfg)) return Promise.reject(sessionError());
    if (typeof window !== "undefined" && (cfg.url || "").startsWith("/subscription/") &&
        ((cfg.method || "get").toLowerCase() !== "get" || response.data?.entitlement_active)) {
      window.dispatchEvent(new Event("fieldlink:billing-changed"));
    }
    if ((cfg.method || "get").toLowerCase() === "get" && !cfg.__flSkipReadCache && isCacheableGet(cfg.url)) {
      saveCache(cacheKey(cfg), response.data);
    }
    return response;
  },
  (error) => {
    if (error.config && staleSession(error.config)) return Promise.reject(sessionError());
    const url = error?.config?.url || "";
    const status = error?.response?.status;
    // No response at all = network failure (offline / no reception).
    if (!error.response && error.config) {
      const cfg = error.config;
      const method = (cfg.method || "get").toLowerCase();
      if (method === "get" && !cfg.__flSkipReadCache && isCacheableGet(cfg.url)) {
        const hit = readCache(cacheKey(cfg));
        if (hit) {
          return Promise.resolve({
            data: hit.data, status: 200, statusText: "OK (offline cache)",
            headers: {}, config: cfg, offline: true,
          });
        }
      }
      if (!cfg.__flReplay && !cfg.__flOnlineOnly && isQueueableWrite(method, cfg.url)) {
        let body = cfg.data;
        if (typeof body === "string") { try { body = JSON.parse(body); } catch { /* keep as-is */ } }
        enqueueWrite({ method, url: cfg.url, data: body });
        return Promise.resolve({
          data: { offline_queued: true }, status: 202, statusText: "Queued offline",
          headers: {}, config: cfg, offline: true,
        });
      }
      // Photos/signatures captured with no reception: stash the blob in
      // IndexedDB and hand back an "offline://" path. The sync run uploads
      // it and rewrites any queued write that references it.
      if (!cfg.__flReplay && !cfg.__flOnlineOnly && method === "post"
          && (cfg.url || "").split("?")[0] === "/files/upload"
          && typeof FormData !== "undefined" && cfg.data instanceof FormData) {
        const file = cfg.data.get("file");
        const category = cfg.data.get("category") || "general";
        if (file) {
          return saveOfflineFile(file, category).then((rec) => ({
            data: {
              id: rec.id, storage_path: `offline://${rec.id}`,
              original_filename: rec.name, content_type: rec.type,
              size: rec.size, offline: true,
            },
            status: 202, statusText: "Stored offline",
            headers: {}, config: cfg, offline: true,
          }));
        }
      }
      return Promise.reject(error);
    }
    if (status === 401) {
      const isLoginish = url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/refresh");
      // /cartrack/* endpoints proxy to an external service; a 401 from them
      // means the upstream Cartrack credentials are bad, NOT that the
      // Fieldlink session expired. Let the page surface the error inline
      // instead of logging the user out.
      const isUpstreamProxy = url.includes("/cartrack/") || url.includes("/xero/");
      if (!isLoginish && !isUpstreamProxy) {
        const cfg = error.config || {};
        // Expired access token? Silently refresh once and retry the call so
        // field workers stay signed in instead of being bounced to /login.
        if (!cfg.__flAuthRetried) {
          return trySilentRefresh().then((tok) => {
            if (staleSession(cfg)) return Promise.reject(sessionError());
            if (tok) {
              cfg.__flAuthRetried = true;
              cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${tok}` };
              return api.request(cfg);
            }
            if (url.includes("/auth/me")) return Promise.reject(error);
            if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
              localStorage.removeItem("fl_token");
              localStorage.removeItem("fl_refresh");
              window.location.replace("/login");
              return new Promise(() => {});
            }
            return Promise.reject(error);
          });
        }
        if (!url.includes("/auth/me") && typeof window !== "undefined") {
          const onLogin = window.location.pathname.startsWith("/login");
          if (!onLogin) {
            window.location.replace("/login");
          }
          // Swallow the error — page is navigating away
          return new Promise(() => {});
        }
      }
    }
    // 402 Payment Required — trial expired or past-due grace exceeded.
    // Send the user to the Billing page so they can pay. /billing itself
    // and /subscription/* are whitelisted on the backend so this won't loop.
    if (status === 402 && typeof window !== "undefined") {
      const detail = error?.response?.data?.detail || {};
      const reason = detail.reason || "subscription_required";
      const path = window.location.pathname;
      const onSafePage = path.startsWith("/app/billing") || path.startsWith("/app/locked") || path.startsWith("/app/support");
      if (!onSafePage) {
        const target = `/app/locked?reason=${encodeURIComponent(reason)}`;
        window.location.replace(target);
        return new Promise(() => {});
      }
    }
    // 503 Maintenance — super-admin has locked the app. Surface a readable
    // message to forms/toasts and flip the whole UI into the maintenance
    // screen for everyone except the super-admin.
    if (status === 503 && error?.response?.data?.detail?.reason === "maintenance") {
      const message = error.response.data.detail.message
        || "The app is temporarily down for maintenance. We'll be back shortly.";
      error.response.data.detail = message;
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("fieldlink:maintenance", { detail: { active: true, message } }));
      }
    }
    if (status === 409 && error?.response?.data?.detail?.code === "organisation_billing_locked") {
      error.response.data.detail = error.response.data.detail.message;
    }
    // 422 validation errors arrive as an array of objects ({type, loc, msg,
    // input, url}) — rendering that in a toast crashes React (#31). Flatten
    // to a readable string so every `e.response?.data?.detail` stays safe.
    const respData = error?.response?.data;
    if (respData?.detail?.code === "seat_limit_reached") {
      respData.seat_limit_detail = respData.detail;
      const d = respData.detail;
      respData.detail = `${d.current_plan_name || d.current_plan}: ${d.current_usage} of ${d.current_limit} active users. ${d.next_plan === "fleet" ? "Contact support about Fleet / custom capacity." : `Upgrade to ${d.next_plan} for up to ${d.next_plan_limit} active users.`}`;
    }
    if (respData && respData.detail != null && typeof respData.detail !== "string") {
      respData.detail = formatApiError(respData.detail);
    }
    return Promise.reject(error);
  },
);

export default api;

// In-flight GET de-duplication: dashboards mount many widgets that request the
// same endpoint at once (e.g. /stats/charts from several cards). Collapsing
// identical concurrent GETs into one network request cuts the initial burst and
// the connection queuing that makes first load feel slow. The shared promise is
// released as soon as it settles, so the next load/refresh fetches fresh data.
const _inflightGets = new Map();
const _origGet = api.get.bind(api);
api.get = (url, config = {}) => {
  // Callers with their own abort signal must not share a request: aborting one
  // caller's signal would cancel the shared promise for everyone else.
  if (config.responseType === "blob" || config.__flSkipReadCache || config.__flNoDedup || config.signal) {
    return _origGet(url, config);
  }
  const key = `${url}::${config.params ? JSON.stringify(config.params) : ""}`;
  const existing = _inflightGets.get(key);
  if (existing) return existing;
  const p = _origGet(url, config).finally(() => _inflightGets.delete(key));
  _inflightGets.set(key, p);
  return p;
};

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
